import type { AnchorLabelResolver } from 'features/libra/comments/anchorLabels'
import { z } from 'zod'

import type { ZoneParams } from './zoneAnalysisApi'

export const ZoneAnchorSchema = z.object({
  type: z.literal('zone'),
  stepId: z.string(),
  zoneId: z.string(),
})

export const ZonePropertyAnchorSchema = z.object({
  type: z.literal('zone-property'),
  stepId: z.string(),
  zoneId: z.string(),
  property: z.string(),
})

export const DamageMechanismAnchorSchema = z.object({
  type: z.literal('damage-mechanism'),
  stepId: z.string(),
  zoneId: z.string(),
  damageMechanismId: z.string(),
})

export const CofAnchorSchema = z.object({
  type: z.literal('cof'),
  stepId: z.string(),
})

export const ZoneAnalysisAnchorSchema = z.discriminatedUnion('type', [
  ZoneAnchorSchema,
  ZonePropertyAnchorSchema,
  DamageMechanismAnchorSchema,
  CofAnchorSchema,
])

export type ZoneAnalysisAnchor = z.infer<typeof ZoneAnalysisAnchorSchema>

function getZoneTitle(zones: ZoneParams[] | undefined, zoneId: string): string {
  return zones?.find((z) => z.id === zoneId)?.title ?? zoneId
}

export const zoneAnchorLabelResolver: AnchorLabelResolver = (anchor, ctx) => {
  const stepName =
    ctx.workflow.steps.find((s) => s.id === anchor.stepId)?.name ??
    anchor.stepId
  const zones = ctx.stepSpecificData?.zones as ZoneParams[] | undefined

  switch (anchor.type) {
    case 'zone':
      return `${stepName} > Zone: ${getZoneTitle(zones, anchor.zoneId)}`
    case 'zone-property': {
      const label = anchor.property
        .split(/[._]/)
        .map((seg) => seg.charAt(0).toUpperCase() + seg.slice(1))
        .join(' > ')
      return `${stepName} > Zone: ${getZoneTitle(zones, anchor.zoneId)} > ${label}`
    }
    case 'damage-mechanism': {
      const zone = zones?.find((z) => z.id === anchor.zoneId)
      const dm = zone?.damage_mechanisms?.find(
        (m) => m.id === anchor.damageMechanismId
      )
      const dmLabel = dm?.name ?? anchor.damageMechanismId
      return `${stepName} > Zone: ${getZoneTitle(zones, anchor.zoneId)} > DM: ${dmLabel}`
    }
    case 'cof':
      return `${stepName} > Consequence of Failure`
    default:
      return undefined
  }
}
